/**
 * eslint-disable @typescript-eslint/naming-convention
 *
 * @format
 */

/** @format */

import { BrandStyliticConfig, StyliticsItem, StyliticsWidget } from '@pdp/data-access/interfaces';
import {
	Component,
	computed,
	ElementRef,
	inject,
	input,
	NgZone,
	OnChanges,
	OnInit,
	PLATFORM_ID
} from '@angular/core';

import { Chain } from '@shared/data-access/interfaces';

import { WINDOW } from '@shared/data-access/services';
import { styliticsConfig } from '../stylitics/stylitics-constants';
import { isPlatformBrowser } from '@angular/common';

@Component({
	selector: 'pdp-component-stylitics-shop-the-set',
	standalone: true,
	templateUrl: './stylitics-shop-the-set.component.html',
	styleUrl: './stylitics-shop-the-set.component.scss'
})
export class StyliticsShopTheSetComponent implements OnInit, OnChanges {
	/** @description The selected color for the product */
	selectedColorAttribute = input<string>('');

	/** @description The current Product */
	ecode = input<string>('');

	productIdForStylitics = computed(() =>
		`${this.ecode()}_${this.selectedColorAttribute()}`.toUpperCase()
	);

	/** @description  The stylitics widget instance */
	private styliticsWidget: StyliticsWidget;

	/** @description Set to true once stylitics content has loaded */
	showHeader = false;

	/** @description The chain identifier abbreviation */
	get chainIdentifier(): string {
		return this.chain.chainIdentifierAbbr;
	}

	/** @description The chain configuration object */
	get chainConfig(): BrandStyliticConfig {
		return styliticsConfig[this.chainIdentifier];
	}

	private window = inject<Window>(WINDOW);
	private chain = inject(Chain);
	private elementRef = inject(ElementRef);
	private readonly ngZone = inject(NgZone);
	private readonly platformId = inject(PLATFORM_ID);

	constructor() {}

	ngOnInit() {
		if (isPlatformBrowser(this.platformId)) {
			this.ngZone.runOutsideAngular(() => {
				this.initializeStylitics();
			});
		}
	}

	ngOnChanges() {
		if (this.styliticsWidget) {
			this.showHeader = false;
			this.styliticsWidget.refresh({
				modules: [
					{
						type: 'shop-the-set',
						params: {
							api: {
								item_number: this.productIdForStylitics()
							}
						}
					}
				]
			});
		}
	}

	/**
	 * @description Initializes the stylitics widget and calls
	 * private helper method to attach event handlers.
	 */
	initializeStylitics() {
		const styliticsScript = document.createElement('script');
		styliticsScript.type = 'text/javascript';
		styliticsScript.src = 'https://web-assets.stylitics.com/loader/latest/composer.js';

		this.elementRef.nativeElement.appendChild(styliticsScript).onload = async () => {
			this.styliticsWidget = await this.window.StyliticsComposer.initialize({
				client: this.chainConfig.name,
				target: 'stylitics-composer-container',
				config: {
					modules: [
						{
							type: 'shop-the-set',
							params: {
								api: {
									item_number: this.productIdForStylitics()
								}
							},
							override: {
								'click.item': (data: { item: StyliticsItem }) => {
									this.onItemClick(data.item);
								}
							},
							on: {
								'mount.set': () => {
									this.showHeader = true;
								}
							}
						}
					]
				}
			});

			this.styliticsWidget.start();
		};
	}

	onItemClick = (item: StyliticsItem) => {
		const remoteId = item?.remote_id ? item.remote_id.split('_')[0] : null;
		const itemColor = item?.other_client_item_ids?.color;
		if (remoteId && itemColor) {
			window.quickview.openProductQuickView(remoteId, 'stylitics', {
				defaultColor: itemColor
			});
		} else if (remoteId) {
			window.quickview.openProductQuickView(remoteId, 'stylitics');
		}
	};
}
